import { GetClientFlags200Response } from "@coeff/api";
import { StatsigProvider, useClientBootstrapInit } from "@statsig/react-bindings";
import { StatsigUser } from "@statsig/react-bindings";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { BuildFlags } from "../constants";
import { AppState } from "../store";

type Props = {
  children: React.ReactNode;
  clientFlags: GetClientFlags200Response;
};

export const StatsigSynchronousProvider: React.FC<Props> = ({ children, clientFlags }) => {
  const { coeffUserEmail, coeffUserId, domain } = useSelector(({ app }: AppState) => ({
    coeffUserEmail: app.coeffUserEmail,
    coeffUserId: app.coeffUserId,
    domain: app.domain,
  }));

  const user: StatsigUser = {
    userID: coeffUserId,
    email: coeffUserEmail,
    custom: {
      ...clientFlags.statsig_user_custom_attributes,
      domain,
    },
    // Must mirror the customIDs the backend embeds in statsig_client_bootstrap_values
    // (get_client_initialize_response passes custom_ids={domainID: domain}). The JS SDK
    // keys the bootstrap cache by the user embedded in the blob but reads it back with
    // this client-side user; a customIDs mismatch misses the cache and drops every gate
    // to its default. See DOMAIN_CUSTOM_ID_TYPE in the server's statsig_utils.
    ...(domain ? { customIDs: { domainID: domain } } : {}),
  };

  const initializeValues = clientFlags.statsig_client_bootstrap_values;
  const serializedValues = JSON.stringify(initializeValues);

  const client = useClientBootstrapInit(BuildFlags.statsig_client_key, user, serializedValues, {
    environment: { tier: BuildFlags.statsig_environment },
  });

  // Seeded with the values already consumed by useClientBootstrapInit so the
  // first effect run is a no-op. Compared as strings to ignore the new object
  // references each React Query refetch produces.
  const lastAppliedRef = useRef<string>(serializedValues);

  useEffect(() => {
    if (serializedValues === lastAppliedRef.current) {
      return;
    }

    lastAppliedRef.current = serializedValues;
    client.dataAdapter.setData(serializedValues);
    // updateUserSync, not initializeSync: the latter is a no-op after the first
    // init; this re-evaluates gate consumers with the refreshed bootstrap data.
    client.updateUserSync(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, serializedValues]);

  return <StatsigProvider client={client}>{children}</StatsigProvider>;
};
